import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Paper,
  CircularProgress,
  InputBase,
  Badge,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Search as SearchIcon,
  Send as SendIcon,
  MoreVert as MoreIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const COLORS = {
  bg: '#F0F2F5',
  sidebar: '#FFFFFF',
  me: '#0084FF',
  them: '#F0F2F5',
  textSec: '#65676B',
  accent: '#0084FF',
};

const Conversation = () => {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [conversationDetail, setConversationDetail] = useState(null);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);

  const scrollRef = useRef(null);
  const { user } = useAuth();
  const rolePath = user?.role === 3 ? 'intervenant' : 'admin';

  const authHeader = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }),
    []
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [conversationDetail?.messages]);

  useEffect(() => {
    fetchList();
  }, [authHeader, rolePath]);

  // FILTRAGE ET TRI (Le plus récent en premier)
  const filtered = useMemo(() => {
    return conversations
      .filter((c) =>
        (c.conversation_label || c.title || '')
          .toLowerCase()
          .includes(search.toLowerCase())
      )
      .sort(
        (a, b) =>
          new Date(b.conversation?.last_message_at) -
          new Date(a.conversation?.last_message_at)
      );
  }, [search, conversations]);

  const openConversation = async (conv) => {
    setSelected(conv);

    try {
      setLoadingChat(true);

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/conversation/${conv.id_conv}`,
        authHeader
      );

      setConversationDetail(res.data);

      // 🔥 MARK AS READ
      await axios.post(
        `${process.env.REACT_APP_API_URL}/conversation/read`,
        { id_conv: conv.id_conv },
        authHeader
      );

      // 🔥 refresh list unread
      fetchList();
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingChat(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || !selected) return;

    const idConv = selected.id_conv;

    const tempMsg = {
      id: Date.now(),
      message,
      created_at: new Date().toISOString(),
      sender_role: 'admin',
      sender: { prenom: 'Moi' },
    };

    setConversationDetail((prev) => ({
      ...prev,
      messages: [...(prev?.messages || []), tempMsg],
    }));

    setMessage('');

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/${rolePath}/message/send`,
        { id_conv: idConv, message },
        authHeader
      );

      // 🔥 refresh inbox after send
      fetchList();
    } catch (err) {
      console.error('SEND ERROR', err);
    }
  };

  const fetchList = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/${rolePath}/conversation/index`,
        authHeader
      );

      setConversations(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box sx={{ p: 3 }}>
      {' '}
      {/* Conteneur global pour le padding */}
      {/* TITRE PRINCIPAL */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: '#2B3674',
          mb: 3,
          letterSpacing: '-0.5px',
        }}
      >
        Conversations Clients
      </Typography>
      {/* TEXTE INFORMATIF / ALERT */}
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        sx={{
          mb: 4,
          borderRadius: '15px',
          bgcolor: '#F4F7FE',
          color: '#2B3674',
          border: '1px solid #E9EDF7',
          '& .MuiAlert-icon': { color: '#4318FF' },
        }}
      >
        <AlertTitle sx={{ fontWeight: 800 }}>
          Gestion du Conversations Clients
        </AlertTitle>
        Suivez les échanges entre vos intervenants et les clients pour les
        réservations
      </Alert>
      <Box
        sx={{
          display: 'flex',
          height: '92vh',
          bgcolor: COLORS.bg,
          p: 2,
          gap: 2,
        }}
      >
        {/* SIDEBAR */}
        <Paper
          elevation={0}
          sx={{
            width: 360,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 4,
            border: '1px solid #E0E0E0',
          }}
        >
          <Box sx={{ p: 2.5 }}>
            <Typography fontWeight={800} fontSize={24} letterSpacing={-0.5}>
              Messages
            </Typography>
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#F0F2F5',
                borderRadius: 3,
                px: 2,
                py: 0.5,
              }}
            >
              <SearchIcon sx={{ fontSize: 20, color: COLORS.textSec }} />
              <InputBase
                fullWidth
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ ml: 1, fontSize: 14 }}
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              filtered.map((c) => (
                <ConversationItem
                  key={c.conversation?.id_conv || c.id_conv}
                  data={c}
                  isActive={
                    (selected?.conversation?.id_conv || selected?.id_conv) ===
                    (c.conversation?.id_conv || c.id_conv)
                  }
                  onClick={() => openConversation(c)}
                />
              ))
            )}
          </Box>
        </Paper>

        {/* CHAT AREA */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 4,
            border: '1px solid #E0E0E0',
            overflow: 'hidden',
          }}
        >
          {!selected ? (
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              sx={{ opacity: 0.5 }}
            >
              <Avatar sx={{ width: 60, height: 60, mb: 2, bgcolor: '#E4E6EB' }}>
                <SendIcon sx={{ color: '#8E8E8E' }} />
              </Avatar>
              <Typography fontWeight={600}>
                Sélectionnez une discussion
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid #F0F2F5',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#FFF',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      bgcolor: COLORS.me,
                      width: 40,
                      height: 40,
                      fontWeight: 700,
                      fontSize: 14,
                    }}
                  >
                    {(selected.conversation_label || selected.title)?.[0]}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={700} fontSize={15}>
                      {selected.conversation_label || selected.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <CircleIcon sx={{ fontSize: 8 }} /> En ligne
                    </Typography>
                  </Box>
                </Stack>
                <IconButton size="small">
                  <MoreIcon />
                </IconButton>
              </Box>

              <Box
                ref={scrollRef}
                sx={{
                  flex: 1,
                  p: 3,
                  overflowY: 'auto',
                  bgcolor: '#FFF',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {loadingChat ? (
                  <Box display="flex" justifyContent="center">
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  conversationDetail?.messages?.map((m) => (
                    <MessageBubble key={m.id} message={m} />
                  ))
                )}
              </Box>

              <Box sx={{ p: 2, bgcolor: '#FFF' }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ bgcolor: '#F0F2F5', borderRadius: 4, px: 2, py: 0.5 }}
                >
                  <InputBase
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Écrire un message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    sx={{ ml: 1, fontSize: 14, py: 1 }}
                  />
                  <IconButton
                    onClick={handleSend}
                    disabled={!message.trim()}
                    sx={{ color: message.trim() ? COLORS.me : COLORS.textSec }}
                  >
                    <SendIcon />
                  </IconButton>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
const ConversationItem = ({ data, isActive, onClick }) => {
  const title = data?.title || data?.context?.title || 'Conversation';

  const conversationLabel = data?.conversation_label || '';

  const contextType = data?.context_type || data?.context?.context_type;

  const lastMsg = data?.last_message?.message || 'Aucun message...';

  const unread = data?.unread_count || 0;

  const time = data?.last_message_at
    ? new Date(data.last_message_at).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5,
        mx: 1,
        mb: 0.5,
        borderRadius: 3,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        bgcolor: isActive ? '#E7F3FF' : 'transparent',
        '&:hover': {
          bgcolor: isActive ? '#E7F3FF' : '#F5F6F7',
        },
      }}
    >
      {/* AVATAR + UNREAD */}
      <Badge
        color="error"
        variant={unread > 0 ? 'standard' : 'dot'}
        badgeContent={unread > 0 ? unread : 0}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            fontWeight: 700,
            bgcolor: isActive ? '#0084FF' : '#E4E6EB',
          }}
        >
          {title?.[0]}
        </Avatar>
      </Badge>

      {/* CONTENT */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* LINE 1: TITLE + TIME */}
        <Stack direction="row" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            {/* CONVERSATION LABEL */}
            {conversationLabel && (
              <Typography fontWeight={700} fontSize={14} noWrap>
                {conversationLabel}
              </Typography>
            )}
            {/* TITLE */}
            <Typography fontWeight={700} fontSize={14} noWrap>
              {title}
            </Typography>
          </Box>

          {/* TIME */}
          <Typography fontSize={10} color="text.secondary">
            {time}
          </Typography>
        </Stack>

        {/* LINE 2: CONTEXT TYPE */}
        {contextType && (
          <Typography fontSize={11} color="primary" noWrap>
            {contextType}
          </Typography>
        )}

        {/* LINE 3: LAST MESSAGE */}
        <Typography fontSize={12} color="text.secondary" noWrap>
          {lastMsg}
        </Typography>
      </Box>
    </Box>
  );
};

const MessageBubble = ({ message }) => {
  const isMe = message.sender_role !== 'client';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isMe ? 'flex-end' : 'flex-start',
        mb: 1,
      }}
    >
      <Box
        sx={{
          p: '10px 16px',
          bgcolor: isMe ? COLORS.me : COLORS.them,
          color: isMe ? '#FFF' : '#000',
          borderRadius: isMe ? '18px 18px 2px 18px' : '18px 18px 18px 2px',
          maxWidth: '70%',
          boxShadow: '0 1px 1px rgba(0,0,0,0.05)',
        }}
      >
        <Typography fontSize={14.5} sx={{ lineHeight: 1.4 }}>
          {message.message}
        </Typography>
        <Typography
          fontSize={9}
          sx={{ mt: 0.5, opacity: 0.7, textAlign: 'right', display: 'block' }}
        >
          {new Date(message.created_at).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
      </Box>
    </Box>
  );
};

export default Conversation;
